import React, { useRef, useState } from "react";
import { connect } from "react-redux";
import { SurveyTypeNotification, LabelValuePair, NotificationLetterDto } from "interfaces/Mueller.Business.Models";
import MCard, { MCardControlMethods } from "components/Shared/MCard.tsx";
import Button from "@mui/material/Button";
import { 
  DATA_TYPE_DROPDOWN2, 
  DATA_TYPE_NUMBER_NEGATIVE_OR_POSITIVE, 
} from "typeConstants/dataTypes";
import * as yup from "yup";
import { MSelectOption } from "components/Shared/MSelect.tsx";

interface AddEditSurveyTypeNotificationProps {
  notification: SurveyTypeNotification | null;  // Pass null to add a new notification
  onCancel: () => void;
  onSave: (notification: SurveyTypeNotification) => void;
  existingNotificationTypes: string[];  // All notification types currently on this survey type. This is used to populate the Previous Notification dropdown to prevent invalid choices
  // Redux - these are fetched in the parent component CustomerSurveyTypeNotifications
  notificationTypes: LabelValuePair[];
  notificationMethods: LabelValuePair[];
  notificationLetters: NotificationLetterDto[];
}

const AddEditSurveyTypeNotification = (props: AddEditSurveyTypeNotificationProps) => {
  const cardControlMethods = useRef<MCardControlMethods>();
  const [letterOptions, setLetterOptions] = useState<MSelectOption[]>([]);
  const [previousOptions, setPreviousOptions] = useState<MSelectOption[]>(props.notificationTypes
    ?.filter(t => props.existingNotificationTypes?.includes(t.value)) ?? []);
  
  const handleSave = () => {
    if (!cardControlMethods.current.runValidation(true)) {
      return;
    }
    const cardData = cardControlMethods.current.getEditedDataObject();
    let notificationData = null;
    if (props.notification) {
      notificationData = { ...props.notification, ...cardData};
    } else {
      notificationData = {
        notificationTypeCode: cardData.notificationTypeCode,
        daysFromPrevious: parseInt(cardData.daysFromPrevious),
        previousNotificationCode: cardData.previousNotificationCode,
        notificationMethodCode: cardData.notificationMethodCode,
        letterCode: cardData.letterCode,
      };
    }
    props.onSave(notificationData);
  };

  const onTypeChange = (option: MSelectOption) => {
    // Limit options to existing notification types and not the selected type
    const filtered = props.notificationTypes
      ?.filter(t => props.existingNotificationTypes?.includes(t.value)
        && t.value !== option.value) ?? [];
    setPreviousOptions(filtered);
  };

  const onMethodChange = (option: MSelectOption) => {
    const filtered = props.notificationLetters
      .filter(l => l.methodCode == option.value && (!l.inactiveDate || l.inactiveDate > new Date()))
      .map(l => ({ label: `${l.code} (${l.name})`, value: l.code }));
    setLetterOptions(filtered);
  };

  const cardFields = [
    {
      key: "notificationTypeCode",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Notification Type",
      value: props.notification?.notificationTypeCode,
      validation: yup.string().required("Notification Type is required"),
      dataTypeOptions: { options: props.notificationTypes, nullable: false },
      onChange: onTypeChange,
    },
    {
      key: "daysFromPrevious",
      dataType: DATA_TYPE_NUMBER_NEGATIVE_OR_POSITIVE,
      label: "Days From Previous",
      value: props.notification?.daysFromPrevious,
    },
    {
      key: "previousNotificationCode",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Previous Notification",
      value: props.notification?.previousNotificationCode,
      dataTypeOptions: { options: previousOptions, nullable: true }
    },
    {
      key: "notificationMethodCode",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Notification Method",
      value: props.notification?.notificationMethodCode,
      // We no longer use Matrix so we shouldn't allow it as a choice
      dataTypeOptions: { options: props.notificationMethods.filter(m => m.value !== "MATRIX" && m.value !== "MATRIX_CUSTOM"), nullable: true },
      onChange: onMethodChange,
    },
    {
      key: "letterCode",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Letter",
      value: props.notification?.letterCode,
      dataTypeOptions: { options: letterOptions, nullable: true }
    },
  ];

  return (
    <MCard
      key="survey-type-notification-edit"
      className="survey-type-notification-edit"
      title="Notification Details"
      onlyEditable
      fields={cardFields}
      getControlMethods={controlMethods => cardControlMethods.current = controlMethods}
    >
      <div className="flex-row-between">
        <Button onClick={handleSave}>Save</Button>
        <Button onClick={props.onCancel}>Cancel</Button>
      </div>
    </MCard>
  );
};

const mapStateToProps = state => ({
  notificationTypes: state.customer.notificationTypes,
  notificationMethods: state.customer.notificationMethods,
  notificationLetters: state.customer.notificationLetters,
});

export default connect(mapStateToProps, null)(AddEditSurveyTypeNotification);